/**
 * Helpers de acceso de invitado a /conference (Sesión Tripartita).
 *
 * El líder no tiene cuenta: entra con `?guestToken=<jwt>`. El guard usa estos
 * helpers para saltar auth/ownership y para detectar un token vencido (mostrar
 * "enlace vencido" en vez del login wall). La verificación real de firma la
 * hace el backend; aquí solo se decodifica el payload para UX.
 */

export function hasGuestToken(to) {
  return to?.name === "Conference" && Boolean(to?.query?.guestToken);
}

/**
 * Decodifica el payload de un JWT. Viaja en base64url (con `-`/`_` y sin
 * padding) y es UTF-8: `atob` devuelve bytes sueltos, así que se pasan por
 * `TextDecoder` para que un nombre como "José Muñoz" no llegue como "JosÃ©".
 *
 * @param {String} token
 * @returns {Object|null} null si el token no se puede decodificar
 */
export function decodeJwtPayload(token) {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch (error) {
    return null;
  }
}

export function isGuestTokenExpired(token, now = Date.now()) {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;
  return payload.exp * 1000 <= now;
}

/**
 * Nombre del invitado, si el token lo declara.
 *
 * El backend incluye `name` en los tokens nuevos. Los emitidos antes —o los de
 * un líder cargado solo con email— no lo traen: ahí devuelve null y el
 * componente elige una etiqueta según el tipo de invitado.
 *
 * @param {String} token
 * @returns {String|null}
 */
export function getGuestName(token) {
  const name = decodeJwtPayload(token)?.name;
  if (typeof name !== "string") return null;
  return name.trim() || null;
}

/**
 * Tipo de invitado (`kind` del token). Hoy solo existe "leader"; el default
 * "guest" evita que un kind inesperado deje la etiqueta sin resolver.
 *
 * @param {String} token
 * @returns {String}
 */
export function getGuestKind(token) {
  return decodeJwtPayload(token)?.kind || "guest";
}
